import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import { InlineAlert, LoadingButton } from "src/grading/components";
import Loader from "src/grading/components/Loader/Loader";
import { PageHeader } from "src/grading/layout/PageHeader";
import { useListSchoolsQuery } from "src/store/reducers/districts/districtsApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";
import {
  ReportRange,
  useGetDistrictReportQuery,
  useGetSchoolReportQuery,
  useLazyGetChallengeSummaryQuery,
} from "src/store/reducers/reports/reportsApiSlice";
import { ExportsCard, RangePicker, ReportBody } from "./ReportWidgets";

/** School or district rollup (FR-062): participation against enrollment, tiers by grade and sex, sessions, active teachers. */
const ScopeReportPage = ({ scope }: { scope: "school" | "district" }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams<{ schoolId?: string; districtId?: string }>();
  const id =
    scope === "school" ? (params.schoolId ?? "") : (params.districtId ?? "");
  const [range, setRange] = useState<ReportRange>({});
  const school = useGetSchoolReportQuery(
    { schoolId: id, range },
    { skip: scope !== "school" },
  );
  const district = useGetDistrictReportQuery(
    { districtId: id, range },
    { skip: scope !== "district" },
  );
  const { data: schools } = useListSchoolsQuery(id, {
    skip: scope !== "district",
  });
  const [challenge, { isFetching: exporting }] =
    useLazyGetChallengeSummaryQuery();
  const [error, setError] = useState<string | null>(null);
  const q = scope === "school" ? school : district;
  const reportName = q.data?.name ?? "";

  useEffect(() => {
    document.title = `${
      scope === "school"
        ? t("reports.schoolReport", { defaultValue: "School report" })
        : t("reports.districtReport", { defaultValue: "District report" })
    } · ${t("app.name")}`;
  }, [scope, t]);

  useEffect(() => {
    dispatch(clearBreadcrumbs());
    dispatch(
      addBreadcrumb({
        label: t("reports.title", { defaultValue: "Reports" }),
        path: "/grading/reports",
      }),
    );
    dispatch(addBreadcrumb({ label: reportName }));
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, reportName, t]);

  const exportChallenge = async () => {
    setError(null);
    try {
      const { data } = await challenge({ districtId: id, range });
      if (data?.exportUrl) window.open(data.exportUrl, "_blank", "noopener");
    } catch (e) {
      setError(
        (e as { data?: { message?: string } })?.data?.message ??
          t("reports.exportSummaryError", {
            defaultValue: "Could not export the participation summary",
          }),
      );
    }
  };

  if (q.isLoading) return <Loader />;
  if (q.isError || !q.data)
    return (
      <Typography color="error">
        {scope === "school"
          ? t("reports.schoolNotFound", {
              defaultValue:
                "No report yet, or this school is outside your scope.",
            })
          : t("reports.districtNotFound", {
              defaultValue:
                "No report yet, or this district is outside your scope.",
            })}
      </Typography>
    );
  const report = q.data;
  const schoolName = (schoolId: string) =>
    schools?.find((s) => s.id === schoolId)?.name ?? schoolId;
  const sub = [
    scope === "school"
      ? t("reports.schoolReport", { defaultValue: "School report" })
      : t("reports.districtReport", { defaultValue: "District report" }),
    t("reports.sessionsCount", {
      defaultValue: "{{count}} sessions",
      count: report.sessions,
    }),
    t("reports.teachersActive", {
      defaultValue: "{{count}} teachers active",
      count: report.activeTeachers,
    }),
  ].join(" · ");

  return (
    <Box>
      <PageHeader title={report.name} description={sub} />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <RangePicker range={range} onChange={setRange} />
        {error && <InlineAlert severity="error">{error}</InlineAlert>}
        <ReportBody
          tested={report.tested}
          enrolled={report.enrolled}
          rate={report.participationRate}
          tiers={report.tiers}
          groups={[
            {
              group: t("reports.groupGrade", { defaultValue: "grade" }),
              rows: report.tiersByGrade,
            },
            {
              group: t("reports.groupBenchmark", {
                defaultValue: "benchmark table",
              }),
              rows: report.tiersBySex,
            },
          ]}
          extra={
            scope === "district" && report.sessionsBySchool ? (
              <Paper
                sx={{ p: "20px 22px", overflowX: "auto" }}
                component="section"
                aria-label={t("reports.sessionsPerSchool", {
                  defaultValue: "Sessions per school",
                })}
              >
                <Typography variant="h4" component="h2" gutterBottom>
                  {t("reports.sessionsPerSchool", {
                    defaultValue: "Sessions per school",
                  })}
                </Typography>
                <Table
                  size="small"
                  aria-label={t("reports.sessionsPerSchool", {
                    defaultValue: "Sessions per school",
                  })}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("console.school")}</TableCell>
                      <TableCell align="right">
                        {t("reports.finalizedSessions", {
                          defaultValue: "Finalized sessions",
                        })}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(report.sessionsBySchool).map(
                      ([schoolId, n]) => (
                        <TableRow key={schoolId}>
                          <TableCell>{schoolName(schoolId)}</TableCell>
                          <TableCell align="right">{n}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() =>
                                history.push(
                                  `/grading/reports/schools/${schoolId}`,
                                )
                              }
                            >
                              {t("reports.schoolReport", {
                                defaultValue: "School report",
                              })}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </Paper>
            ) : undefined
          }
          exports={
            scope === "district" ? (
              <ExportsCard>
                <LoadingButton
                  variant="outlined"
                  loading={exporting}
                  onClick={exportChallenge}
                  label={t("reports.exportParticipation", {
                    defaultValue: "Participation summary (XLSX)",
                  })}
                  sx={{ borderRadius: `${radius.pill}px` }}
                />
              </ExportsCard>
            ) : undefined
          }
        />
      </Box>
    </Box>
  );
};

export const SchoolReportPage = () => <ScopeReportPage scope="school" />;
export const DistrictReportPage = () => <ScopeReportPage scope="district" />;
export default ScopeReportPage;
